import { ACTION_PAYMENT } from '@/redux/actions';
import { Icon } from '@iconify/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { Badge } from '@/components/atoms/Badge';
import Button from '@/components/atoms/Button/Button';
import ImgWithFallback from '@/components/atoms/ImgWithFallback';
import Modal from '@/components/atoms/Modal/Modal';
import { Separator } from '@/components/atoms/Separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/atoms/Sheets';
import PATH from '@/routes/path';
import { CONSTANTS, HELPERS } from '@/utils';

import CartCleanupNotification from '@/components/molecules/CartCleanupNotification/CartCleanupNotification';
import { useRemoveFromCart } from '@/hooks/cart/useCartMutations';
import { useCart } from '@/hooks/useCart';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to get validation details for a specific item
const getItemValidationDetails = (itemId, validation) => {
  if (!validation || !validation.details) return null;

  // Check in unavailable_items
  const unavailableItem = validation.details.unavailable_items?.find(
    (item) => item.item?.purchasable_uuid === itemId || item.item?.id === itemId
  );

  if (unavailableItem) {
    return {
      type: 'unavailable',
      message: unavailableItem.reason || 'Item tidak tersedia',
      details: unavailableItem.details || 'Item ini tidak dapat dibeli saat ini',
    };
  }

  // Check in removed_items
  const removedItem = validation.details.removed_items?.find(
    (item) => item.item?.purchasable_uuid === itemId || item.item?.id === itemId
  );

  if (removedItem) {
    return {
      type: 'removed',
      message: removedItem.reason || 'Item telah dihapus',
      details: removedItem.details || 'Item ini sudah tidak tersedia',
    };
  }

  return null;
};

export default function ShoppingCart({ onCartUpdate, isDesktopLayout, isVisible, onHideRequest }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const voucherDataFromStore = useSelector((state) => state.payment.voucher);
  const isLoadingVoucher = useSelector((state) => state.payment.isLoadingVoucher);

  const [voucherCodeInput, setVoucherCodeInput] = useState('');
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [isVoucherCheckAttempted, setIsVoucherCheckAttempted] = useState(false);
  const [showVoucherInfoMessage, setShowVoucherInfoMessage] = useState(false);
  const [voucherJustApplied, setVoucherJustApplied] = useState(false);

  // 🎯 NEW: State declarations first
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [sheetIsOpen, setSheetIsOpen] = useState(false);

  // 🎯 NEW: Delete modal state
  const [deleteModalInfo, setDeleteModalInfo] = useState({
    isOpen: false,
    itemToDelete: null,
    isMultiple: false,
  });

  // 🎯 ENHANCED: Use improved cart hook
  const {
    data: cartData,
    isLoading: loading,
    isError: hasError,
    error: cartError,
    cartItems,
    totalItems,
    totalQuantity,
    isEmpty,
    hasValidationIssues,
    validation,
    cleanup,
    refreshCart,
  } = useCart();

  const { mutate: removeFromCart } = useRemoveFromCart();

  const error = hasError ? 'Gagal memuat item keranjang. Silakan coba lagi nanti.' : null;

  // 🎯 ENHANCED: Better item deduplication and grouping
  const uniqueCartItems = useMemo(() => {
    if (!cartItems || cartItems.length === 0) return [];

    const uniqueItems = new Map();

    cartItems.forEach((item) => {
      if (uniqueItems.has(item.id)) {
        const existingItem = uniqueItems.get(item.id);
        existingItem.quantity += item.quantity || 1;
      } else {
        uniqueItems.set(item.id, { ...item });
      }
    });

    return Array.from(uniqueItems.values());
  }, [cartItems]);

  // 🎯 ENHANCED: Grouping logic for mobile
  const groupedItems = useMemo(() => {
    if (!Array.isArray(uniqueCartItems)) return {};

    return uniqueCartItems.reduce((acc, item) => {
      const cat = item.itemType;
      acc[cat] = acc[cat] || [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [uniqueCartItems]);

  // 🎯 ENHANCED: Auto-refresh on mobile sheet open (with stable ref)
  const refreshCartRef = useRef(refreshCart);
  refreshCartRef.current = refreshCart;

  useEffect(() => {
    if (sheetIsOpen && !isDesktopLayout) {
      refreshCartRef.current();
    }
  }, [sheetIsOpen, isDesktopLayout]);

  const stableItemIds = useMemo(() => {
    return uniqueCartItems
      .map((item) => item.id)
      .sort()
      .join(',');
  }, [uniqueCartItems]);

  const validSelectedItemIds = useMemo(() => {
    const currentItemIds = new Set(uniqueCartItems.map((item) => item.id));
    const filteredIds = new Set([...selectedItemIds].filter((id) => currentItemIds.has(id)));

    if (uniqueCartItems.length === 0 && selectedItemIds.size > 0) {
      return new Set();
    }

    return filteredIds;
  }, [uniqueCartItems, selectedItemIds]);

  useEffect(() => {
    if (
      validSelectedItemIds.size !== selectedItemIds.size ||
      ![...validSelectedItemIds].every((id) => selectedItemIds.has(id))
    ) {
      setSelectedItemIds(validSelectedItemIds);
    }
  }, [validSelectedItemIds]);

  // 🎯 NEW: Selection handlers
  const handleSelectItem = (itemId) => {
    setSelectedItemIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      if (newSet.size > 0) {
        setShowVoucherInfoMessage(false);
      }
      return newSet;
    });
  };

  const handleSelectAllGlobal = (checked) => {
    if (checked) {
      // Only select available items (exclude items with validation issues)
      const availableItems = uniqueCartItems.filter((item) => {
        const itemValidation = getItemValidationDetails(item.id, validation);
        return itemValidation === null;
      });
      setSelectedItemIds(new Set(availableItems.map((item) => item.id)));
      setShowVoucherInfoMessage(false);
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const handleSelectAllInCategory = (categoryItems, shouldSelect) => {
    setSelectedItemIds((prev) => {
      const newSet = new Set(prev);
      categoryItems.forEach((item) => {
        const itemValidation = getItemValidationDetails(item.id, validation);
        const isUnavailable = itemValidation !== null;

        // Skip unavailable items
        if (isUnavailable) {
          return;
        }

        if (shouldSelect) {
          newSet.add(item.id);
        } else {
          newSet.delete(item.id);
        }
      });
      if (newSet.size > 0) {
        setShowVoucherInfoMessage(false);
      }
      return newSet;
    });
  };

  const selectedItemsArray = useMemo(() => {
    return uniqueCartItems.filter((item) => {
      if (!selectedItemIds.has(item.id)) return false;
      // Exclude items with validation issues from selected items
      const itemValidation = getItemValidationDetails(item.id, validation);
      return itemValidation === null;
    });
  }, [uniqueCartItems, selectedItemIds, validation]);

  const priceBreakdown = useMemo(() => {
    const selectedItems = uniqueCartItems.filter((item) => selectedItemIds.has(item.id));

    let originalPriceTotal = 0;
    let itemDiscountTotal = 0;
    let currentPriceTotal = 0;

    selectedItems.forEach((item) => {
      const quantity = item.quantity || 1;
      const currentPrice = Number(item.price) || 0;
      const originalPrice = Number(item.originalPrice) || currentPrice;

      currentPriceTotal += currentPrice * quantity;
      originalPriceTotal += originalPrice * quantity;

      if (originalPrice > currentPrice) {
        itemDiscountTotal += (originalPrice - currentPrice) * quantity;
      }
    });

    return {
      originalPriceTotal,
      itemDiscountTotal,
      currentPriceTotal,
      hasItemDiscounts: itemDiscountTotal > 0,
    };
  }, [uniqueCartItems, selectedItemIds]);

  const subtotalBeforeVoucher = useMemo(() => {
    return selectedItemsArray.reduce((sum, item) => {
      // Double-check: only count available items
      const itemValidation = getItemValidationDetails(item.id, validation);
      if (itemValidation !== null) return sum;
      return sum + Number(item.price) * item.quantity;
    }, 0);
  }, [selectedItemsArray, validation]);

  // ✅ Optimized: Replace voucher processing useEffect with useMemo for better performance
  const processedVoucher = useMemo(() => {
    if (!isVoucherCheckAttempted || !voucherDataFromStore) return null;

    if (voucherDataFromStore.payload && voucherDataFromStore.payload.code) {
      const currentSubtotal = subtotalBeforeVoucher;
      let discountAmount = 0;

      const discountValue = voucherDataFromStore.payload.discount_value || voucherDataFromStore.payload.discount;
      const discountType = voucherDataFromStore.payload.discount_type;

      if (discountType === 'percentage') {
        discountAmount = currentSubtotal * (discountValue / 100);
      } else if (discountType === 'fixed_amount') {
        discountAmount = discountValue;
      } else if (discountValue) {
        if (discountValue <= 100) {
          discountAmount = currentSubtotal * (discountValue / 100);
        } else {
          discountAmount = discountValue;
        }
      }

      if (voucherDataFromStore.payload.max_amount) {
        discountAmount = Math.min(discountAmount, voucherDataFromStore.payload.max_amount);
      }

      discountAmount = Math.min(discountAmount, currentSubtotal);
      discountAmount = Math.floor(discountAmount);

      return {
        code: voucherDataFromStore.payload.code,
        discountAmount: discountAmount,
        details: voucherDataFromStore.payload,
      };
    }

    return null;
  }, [voucherDataFromStore, isVoucherCheckAttempted, subtotalBeforeVoucher]);

  // ✅ Optimized: Handle voucher state updates efficiently
  useEffect(() => {
    if (processedVoucher) {
      setAppliedVoucher(processedVoucher);

      if (processedVoucher.discountAmount > 0) {
        setVoucherJustApplied(true);
        setTimeout(() => setVoucherJustApplied(false), 2000);
      }
    } else if (isVoucherCheckAttempted && voucherDataFromStore) {
      setAppliedVoucher(null);
    }

    if (!isLoadingVoucher && isVoucherCheckAttempted) {
      setIsVoucherCheckAttempted(false);
    }
  }, [processedVoucher, isVoucherCheckAttempted, voucherDataFromStore, isLoadingVoucher]);

  // Calculate final price after applying voucher
  const finalPrice = useMemo(() => {
    if (appliedVoucher?.discountAmount >= 0) {
      return Math.max(0, subtotalBeforeVoucher - appliedVoucher.discountAmount);
    }
    return subtotalBeforeVoucher;
  }, [subtotalBeforeVoucher, appliedVoucher]);

  const totalQuantityInCart = useMemo(() => {
    return uniqueCartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [uniqueCartItems]);

  const dropdownRef = useRef(null);

  // 🎯 FIXED: Stable ref for onHideRequest to prevent infinite loops
  const onHideRequestRef = useRef(onHideRequest);
  onHideRequestRef.current = onHideRequest;

  useEffect(() => {
    if (isDesktopLayout && isVisible) {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          const cartIcon = document.querySelector('[aria-label="Keranjang Belanja"]');
          if (cartIcon && cartIcon.contains(event.target)) {
            return;
          }
          if (onHideRequestRef.current) onHideRequestRef.current();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, isDesktopLayout]);

  // 🎯 FIXED: Memoize onCartUpdate to prevent infinite loops
  const onCartUpdateRef = useRef(onCartUpdate);
  onCartUpdateRef.current = onCartUpdate;

  useEffect(() => {
    if (onCartUpdateRef.current) {
      onCartUpdateRef.current(totalQuantityInCart);
    }
  }, [totalQuantityInCart]);

  const removeItem = (id) => {
    removeFromCart(id);
  };

  // 🎯 ENHANCED: Voucher handling
  const handleApplyVoucher = (event) => {
    if (event) event.preventDefault();
    if (selectedItemIds.size === 0) {
      setShowVoucherInfoMessage(true);
      return;
    }
    setShowVoucherInfoMessage(false);
    if (voucherCodeInput.trim()) {
      setIsVoucherCheckAttempted(true);
      setAppliedVoucher(null);
      dispatch(ACTION_PAYMENT.getVoucherCode(voucherCodeInput.trim()));
    }
  };

  const handleVoucherInputFocus = () => {
    if (selectedItemIds.size === 0) {
      setShowVoucherInfoMessage(true);
    } else {
      setShowVoucherInfoMessage(false);
    }
  };

  const handleRemoveVoucher = () => {
    setVoucherCodeInput('');
    setAppliedVoucher(null);
    setIsVoucherCheckAttempted(false);
    setShowVoucherInfoMessage(false);
  };

  const handleNavigateToPayment = () => {
    const paymentData = {
      cartItems: selectedItemsArray,
      voucherCode: appliedVoucher?.code || null,
      appliedVoucherDetails: appliedVoucher,
      totalPrice: finalPrice,
    };

    const CART_STORAGE_KEY = 'CART_PAYMENT_DATA';
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(paymentData));

    // Close sheet and navigate
    setSheetIsOpen(false);
    history.push(PATH.PAYMENT);
  };

  const hasSelectedItemIssues = selectedItemsArray.some((item) => {
    const itemValidation = getItemValidationDetails(item.id, validation);
    return itemValidation !== null;
  });
  const isCheckoutDisabled = selectedItemsArray.length === 0 || hasSelectedItemIssues;

  // 🎯 NEW: Delete modal handlers
  const openDeleteConfirmationModal = (itemId) => {
    const item = uniqueCartItems.find((cartItem) => cartItem.id === itemId);
    if (item) {
      setDeleteModalInfo({ isOpen: true, itemToDelete: item, isMultiple: false });
    }
  };

  const openDeleteSelectedConfirmationModal = () => {
    if (selectedItemIds.size === 0) return;

    if (selectedItemIds.size === 1) {
      const singleItemId = selectedItemIds.values().next().value;
      const item = uniqueCartItems.find((cartItem) => cartItem.id === singleItemId);
      if (item) {
        setDeleteModalInfo({ isOpen: true, itemToDelete: item, isMultiple: false });
      }
    } else {
      setDeleteModalInfo({ isOpen: true, itemToDelete: null, isMultiple: true });
    }
  };

  const handleConfirmDelete = () => {
    if (deleteModalInfo.isMultiple) {
      selectedItemIds.forEach((id) => {
        removeFromCart(id);
      });
    } else if (deleteModalInfo.itemToDelete) {
      removeFromCart(deleteModalInfo.itemToDelete.id);
    }
    setDeleteModalInfo({ isOpen: false, itemToDelete: null, isMultiple: false });
  };

  const handleCancelDelete = () => {
    setDeleteModalInfo({ isOpen: false, itemToDelete: null, isMultiple: false });
  };

  const MiniCartDropdownContent = () => (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-2xl bg-white shadow-lg rounded-md overflow-hidden z-50 border border-gray-200"
      style={{ maxHeight: 'calc(100vh - 100px)' }}
      onMouseEnter={onCartUpdate ? () => onCartUpdate(totalQuantityInCart, true) : undefined}
      onMouseLeave={onCartUpdate ? () => onCartUpdate(totalQuantityInCart, false) : undefined}
    >
      <div className="px-4 py-3 border-b flex justify-between items-center">
        <h3 className="font-medium text-gray-900">Keranjang ({totalQuantityInCart})</h3>
        <Link
          to={PATH.CART}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
          onClick={() => {
            if (onHideRequest) onHideRequest();
          }}
        >
          Lihat semua
        </Link>
      </div>

      <div className="max-h-80 overflow-y-auto p-2 cart-scrollbar">
        {loading && uniqueCartItems.length === 0 ? (
          <div className="flex items-center justify-center p-4">
            <Icon icon="eos-icons:loading" className="h-8 w-8 text-primary-500" />
          </div>
        ) : error ? (
          <div className="text-center p-4">
            <Icon icon="feather:alert-triangle" className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : uniqueCartItems.length === 0 ? (
          <div className="text-center p-4">
            <Icon icon="feather:shopping-cart" className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Keranjang Anda kosong.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {uniqueCartItems.map((item) => {
              const itemValidation = getItemValidationDetails(item.id, validation);
              const isUnavailable = itemValidation !== null;

              return (
                <div
                  key={item.id}
                  className={`px-6 pb-6 pt-4 rounded hover:bg-gray-50 flex items-center justify-between gap-3 ${
                    isUnavailable ? 'opacity-75 bg-red-50 border border-red-200' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0 ${
                        isUnavailable ? 'grayscale' : ''
                      }`}
                    >
                      {(!item.image || item.image === '') ? (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.itemType === 'mentoring' ? (
                            <div className="w-full h-full flex items-center justify-center bg-purple-100">
                              <Icon icon="material-symbols:psychology" className="w-8 h-8 text-purple-600" />
                            </div>
                          ) : item.itemType === 'learning_program' ? (
                            <div className="w-full h-full flex items-center justify-center bg-blue-100">
                              <Icon icon="material-symbols:auto-stories" className="w-8 h-8 text-blue-600" />
                            </div>
                          ) : item.itemType === 'learningPath' ? (
                            <div className="w-full h-full flex items-center justify-center bg-green-100">
                              <Icon icon="material-symbols:school" className="w-8 h-8 text-green-600" />
                            </div>
                          ) : item.itemType === 'course' ? (
                            <div className="w-full h-full flex items-center justify-center bg-indigo-100">
                              <Icon icon="material-symbols:play-lesson" className="w-8 h-8 text-indigo-600" />
                            </div>
                          ) : item.itemType === 'event' ? (
                            <div className="w-full h-full flex items-center justify-center bg-orange-100">
                              <Icon icon="material-symbols:event" className="w-8 h-8 text-orange-600" />
                            </div>
                          ) : item.itemType === 'subscription' || item.itemType === 'subscription_package' ? (
                            <div className="w-full h-full flex items-center justify-center bg-pink-100">
                              <Icon icon="material-symbols:subscriptions" className="w-8 h-8 text-pink-600" />
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <Icon icon="material-symbols:shopping-bag" className="w-8 h-8 text-gray-600" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <ImgWithFallback
                          src={item.image}
                          width={64}
                          height={64}
                          fallbackSrc={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex flex-col max-w-1/3">
                      <div className="flex items-start justify-between">
                        <div className="flex flex-wrap gap-1">
                          <Badge
                            itemType={isUnavailable ? null : item.itemType}
                            variant={isUnavailable ? 'outline' : undefined}
                            className={`text-xs font-normal px-1.5 py-0.5 mb-1 mr-1 inline-block ${
                              isUnavailable ? 'bg-gray-100 text-gray-400 border-gray-200' : ''
                            }`}
                          >
                            {CONSTANTS.CATEGORY_DISPLAY_NAMES[item.itemType] || item.itemType}
                          </Badge>
                        </div>
                      </div>
                      <h4
                        className={`text-sm font-medium truncate line-clamp-2 text-wrap leading-tight ${
                          isUnavailable ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      >
                        {item.name}
                      </h4>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      {item.discount > 0 && !isUnavailable && (
                        <Badge
                          variant="destructive_outline"
                          className="text-xs font-normal px-1.5 py-0.5 mb-1 inline-block"
                        >
                          {item.discount > 0 ? `Disc ${item.discount}%` : ''}
                        </Badge>
                      )}
                      {item.originalPrice && !isUnavailable && (
                        <p className="text-xs text-gray-400 line-through">
                          {formatCurrency(item.originalPrice * item.quantity)}
                        </p>
                      )}
                    </div>
                    {isUnavailable ? (
                      <p className="text-sm font-medium text-gray-app">Item ini tidak tersedia</p>
                    ) : (
                      <p className="text-sm font-medium text-primary-600">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  if (isDesktopLayout) {
    return isVisible ? <MiniCartDropdownContent /> : null;
  }

  // 🎯 ENHANCED: Mobile Sheet implementation with full functionality
  return (
    <Sheet open={sheetIsOpen} onOpenChange={setSheetIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant={Button.VARIANTS.GRAY_OUTLINE}
          size={Button.SIZES.SQUARE}
          className="relative"
          aria-label="Buka Keranjang Belanja"
          onClick={() => setSheetIsOpen(true)}
        >
          <Icon icon="feather:shopping-cart" className="h-5 w-5 text-primary-500" />
          {totalQuantityInCart > 0 && !loading && (
            <Badge
              variant="secondary"
              className="absolute -top-2 -right-2 text-xs px-1.5 min-w-5 h-5 flex items-center justify-center rounded-full"
            >
              {totalQuantityInCart}
            </Badge>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-md p-0 flex flex-col bg-white">
        <SheetHeader className="px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <button
              onClick={() => setSheetIsOpen(false)}
              className="mr-2 rounded-full p-1 hover:bg-gray-100"
              aria-label="Tutup Keranjang"
            >
              <Icon icon="feather:chevron-left" className="h-5 w-5 text-gray-500" />
            </button>
            <SheetTitle className="flex items-center text-lg font-semibold">
              <Icon icon="feather:shopping-cart" className="mr-2 h-5 w-5 text-primary-500" />
              Keranjang Belanja
              {!loading && !error && (
                <span className="ml-2 text-sm font-normal text-gray-500">({totalQuantityInCart} item)</span>
              )}
            </SheetTitle>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto cart-scrollbar">
          {loading && uniqueCartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] px-6 py-10">
              <Icon icon="eos-icons:loading" className="h-10 w-10 text-primary-500 mb-4" />
              <p className="text-gray-500">Memuat keranjang...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] px-6 py-10 text-center">
              <Icon icon="feather:alert-triangle" className="h-10 w-10 text-red-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">Oops, terjadi kesalahan</h3>
              <p className="text-gray-500 mb-6">{error}</p>
            </div>
          ) : uniqueCartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] px-6 py-10 text-center">
              <div className="bg-gray-100 p-6 rounded-full mb-4">
                <Icon icon="feather:shopping-cart" className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium mb-2">Keranjang Anda kosong</h3>
              <p className="text-gray-500 mb-6">Tambahkan beberapa produk untuk melanjutkan belanja.</p>
              <Button
                onClick={() => setSheetIsOpen(false)}
                variant={Button.VARIANTS.PRIMARY}
                className="text-white"
              >
                Lanjutkan Belanja
              </Button>
            </div>
          ) : (
            <div className="px-4 py-4 space-y-4">
              {/* 🎯 NEW: Cart Cleanup Notification */}
              <CartCleanupNotification cleanup={cleanup} />

              {/* 🎯 NEW: Global select all section with bulk delete */}
              <div className="flex justify-between items-center pb-2 border-b">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="select-all-mobile"
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mr-2"
                    checked={
                      uniqueCartItems.length > 0 &&
                      uniqueCartItems.filter((item) => {
                        const itemValidation = getItemValidationDetails(item.id, validation);
                        return itemValidation === null;
                      }).length > 0 &&
                      selectedItemIds.size ===
                        uniqueCartItems.filter((item) => {
                          const itemValidation = getItemValidationDetails(item.id, validation);
                          return itemValidation === null;
                        }).length
                    }
                    onChange={(e) => handleSelectAllGlobal(e.target.checked)}
                    aria-label="Pilih semua item"
                  />
                  <label htmlFor="select-all-mobile" className="text-sm font-semibold">
                    Pilih Semua{' '}
                    <span className="text-gray-500">
                      ({selectedItemsArray.length} / {uniqueCartItems.length} terpilih)
                    </span>
                  </label>
                </div>
                {selectedItemsArray.length > 0 && (
                  <Button
                    variant={Button.VARIANTS.GHOST}
                    onClick={openDeleteSelectedConfirmationModal}
                    className="text-red-500 hover:text-red-700 text-xs"
                    size={Button.SIZES.SMALL}
                  >
                    <Icon icon="feather:trash-2" className="h-3 w-3 mr-1" />
                    Hapus ({selectedItemsArray.length})
                  </Button>
                )}
              </div>

              {/* 🎯 NEW: Grouped items with category selection */}
              {Object.entries(groupedItems).map(([category, items]) => {
                const availableItemsInCategory = items.filter((item) => {
                  const itemValidation = getItemValidationDetails(item.id, validation);
                  return itemValidation === null;
                });
                const allInCategorySelected =
                  availableItemsInCategory.length > 0 &&
                  availableItemsInCategory.every((item) => selectedItemIds.has(item.id));
                return (
                  <div key={category} className="mb-4">
                    <div className="flex items-center mb-2">
                      {items.length > 0 && (
                        <input
                          type="checkbox"
                          id={`select-category-${category}`}
                          className="h-3 w-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mr-2"
                          checked={allInCategorySelected}
                          onChange={(e) => handleSelectAllInCategory(items, e.target.checked)}
                          disabled={availableItemsInCategory.length === 0}
                          aria-label={`Pilih semua ${CONSTANTS.CATEGORY_DISPLAY_NAMES[category] || category}`}
                        />
                      )}
                      <h3 className="text-sm font-semibold text-gray-700">
                        {CONSTANTS.CATEGORY_DISPLAY_NAMES[category] || category} ({items.length})
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {items.map((item) => {
                        const itemValidation = getItemValidationDetails(item.id, validation);
                        const isUnavailable = itemValidation !== null;

                        return (
                          <div
                            key={item.id}
                            className={`flex gap-3 p-3 rounded-lg border transition-all ${
                              selectedItemIds.has(item.id) && !isUnavailable
                                ? 'bg-primary-50 border-primary-200'
                                : 'bg-white border-gray-200'
                            } ${isUnavailable ? 'opacity-75 border-red-200' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-1 flex-shrink-0"
                              checked={selectedItemIds.has(item.id) && !isUnavailable}
                              onChange={() => !isUnavailable && handleSelectItem(item.id)}
                              disabled={isUnavailable}
                              aria-label={`Pilih ${item.name}`}
                            />

                            <div
                              className={`flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden ${isUnavailable ? 'grayscale' : ''}`}
                            >
                              {(!item.image || item.image === '') ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  {item.itemType === 'mentoring' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-purple-100">
                                      <Icon icon="material-symbols:psychology" className="w-8 h-8 text-purple-600" />
                                    </div>
                                  ) : item.itemType === 'learning_program' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-blue-100">
                                      <Icon icon="material-symbols:auto-stories" className="w-8 h-8 text-blue-600" />
                                    </div>
                                  ) : item.itemType === 'learningPath' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-green-100">
                                      <Icon icon="material-symbols:school" className="w-8 h-8 text-green-600" />
                                    </div>
                                  ) : item.itemType === 'course' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-indigo-100">
                                      <Icon icon="material-symbols:play-lesson" className="w-8 h-8 text-indigo-600" />
                                    </div>
                                  ) : item.itemType === 'event' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-orange-100">
                                      <Icon icon="material-symbols:event" className="w-8 h-8 text-orange-600" />
                                    </div>
                                  ) : item.itemType === 'subscription' || item.itemType === 'subscription_package' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-pink-100">
                                      <Icon icon="material-symbols:subscriptions" className="w-8 h-8 text-pink-600" />
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                      <Icon icon="material-symbols:shopping-bag" className="w-8 h-8 text-gray-600" />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <ImgWithFallback
                                  src={item.image}
                                  width={64}
                                  height={64}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap gap-1 mb-1">
                                <Badge
                                  itemType={isUnavailable ? null : item.itemType}
                                  variant={isUnavailable ? 'outline' : undefined}
                                  className={`text-xs font-normal px-1.5 py-0.5 ${
                                    isUnavailable ? 'bg-gray-100 text-gray-400 border-gray-200' : ''
                                  }`}
                                >
                                  {CONSTANTS.CATEGORY_DISPLAY_NAMES[item.itemType] || item.itemType}
                                </Badge>
                              </div>
                              <h4
                                className={`text-sm font-medium line-clamp-2 mb-1 ${
                                  isUnavailable ? 'text-gray-400' : 'text-gray-900'
                                }`}
                              >
                                {item.name}
                              </h4>

                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="flex items-center gap-1 mb-1">
                                    {item.discount > 0 && !isUnavailable && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs px-1 py-0.5 text-red-600 border-red-200 bg-red-50"
                                      >
                                        {`-${item.discount}%`}
                                      </Badge>
                                    )}
                                    {item.discountText && !item.discount && !isUnavailable && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs px-1 py-0.5 text-red-600 border-red-200 bg-red-50"
                                      >
                                        {item.discountText}
                                      </Badge>
                                    )}
                                  </div>
                                  {item.originalPrice &&
                                    item.originalPrice > 0 &&
                                    item.originalPrice > item.price &&
                                    !isUnavailable && (
                                      <div className="text-xs text-gray-400 line-through mb-1">
                                        {formatCurrency(item.originalPrice * item.quantity)}
                                      </div>
                                    )}
                                  {isUnavailable ? (
                                    <div className="text-sm font-semibold text-gray-app">
                                      Item ini tidak tersedia
                                    </div>
                                  ) : (
                                    <div className="text-sm font-semibold text-primary-600">
                                      {formatCurrency(item.price * item.quantity)}
                                    </div>
                                  )}
                                  {item.quantity > 1 && (
                                    <div className="text-xs text-gray-500">
                                      {formatCurrency(item.price)} / item
                                    </div>
                                  )}
                                </div>

                                <button
                                  onClick={() => openDeleteConfirmationModal(item.id)}
                                  className="p-1 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 flex-shrink-0"
                                  aria-label={`Hapus ${item.name} dari keranjang`}
                                >
                                  <Icon icon="feather:trash-2" className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 🎯 ENHANCED: Checkout section with detailed price breakdown */}
        {uniqueCartItems.length > 0 && !loading && !error && (
          <div className="border-t bg-gray-50">
            <div className="px-6 py-4">
              {/* 🎯 NEW: Enhanced Summary section with price breakdown */}
              <div className="space-y-2 mb-4">
                {/* Show original price if there are item discounts */}
                {priceBreakdown.hasItemDiscounts && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Subtotal Harga Asli ({selectedItemsArray.length} item)
                    </span>
                    <span className="text-sm text-gray-800">
                      {formatCurrency(priceBreakdown.originalPriceTotal)}
                    </span>
                  </div>
                )}

                {/* Show item discounts if any */}
                {priceBreakdown.hasItemDiscounts && (
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Diskon Item</span>
                    <span className="text-sm font-semibold">
                      - {formatCurrency(priceBreakdown.itemDiscountTotal)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {priceBreakdown.hasItemDiscounts
                      ? 'Subtotal Setelah Diskon'
                      : `Subtotal (${selectedItemsArray.length} item)`}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">
                    {formatCurrency(subtotalBeforeVoucher)}
                  </span>
                </div>

                {appliedVoucher && (
                  <div
                    className={`flex justify-between items-center text-green-600 transition-all duration-500 ${
                      voucherJustApplied ? 'bg-green-50 scale-105 shadow-sm rounded-lg px-3 py-2 -mx-3' : ''
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm">
                      {voucherJustApplied && <span className="text-green-500 animate-bounce">🎉</span>}
                      Diskon Voucher ({appliedVoucher.code})
                    </span>
                    <span
                      className={`text-sm font-semibold transition-all duration-300 ${
                        voucherJustApplied ? 'text-green-700 font-bold' : ''
                      }`}
                    >
                      - {formatCurrency(appliedVoucher.discountAmount)}
                    </span>
                  </div>
                )}
              </div>

              {/* 🎯 ENHANCED: Voucher section */}
              <div className="mb-4">
                <label htmlFor="voucher-mobile" className="block text-sm font-medium text-gray-700 mb-1">
                  Kode Voucher
                </label>
                <form onSubmit={handleApplyVoucher} className="flex items-center gap-2">
                  <input
                    type="text"
                    id="voucher-mobile"
                    value={voucherCodeInput}
                    onChange={(e) => {
                      setVoucherCodeInput(e.target.value);
                      if (appliedVoucher) setAppliedVoucher(null);
                      setIsVoucherCheckAttempted(false);
                      if (selectedItemIds.size > 0) {
                        setShowVoucherInfoMessage(false);
                      }
                    }}
                    onFocus={handleVoucherInputFocus}
                    placeholder="Kode Voucher"
                    className={`flex-grow appearance-none block w-full px-3 py-2 border border-gray-300 rounded-full shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm ${selectedItemIds.size === 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={selectedItemIds.size === 0 || appliedVoucher !== null || isLoadingVoucher}
                  />
                  {appliedVoucher ? (
                    <Button
                      type="button"
                      variant={Button.VARIANTS.GHOST}
                      onClick={handleRemoveVoucher}
                      className="text-red-500 whitespace-nowrap rounded-full text-xs"
                      disabled={isLoadingVoucher}
                    >
                      Hapus
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant={Button.VARIANTS.GRAY_OUTLINE}
                      className="rounded-full whitespace-nowrap text-xs"
                      disabled={selectedItemIds.size === 0 || !voucherCodeInput.trim() || isLoadingVoucher}
                      isLoading={isLoadingVoucher}
                    >
                      {isLoadingVoucher ? 'Cek...' : 'Gunakan'}
                    </Button>
                  )}
                </form>

                {/* Voucher messages */}
                {showVoucherInfoMessage && (
                  <p className="text-blue-600 text-xs mt-1">
                    Pilih item terlebih dahulu untuk menggunakan voucher.
                  </p>
                )}
                {!isLoadingVoucher && isVoucherCheckAttempted && appliedVoucher && !showVoucherInfoMessage && (
                  <p className="text-green-600 text-xs mt-1">
                    Voucher "{appliedVoucher.code}" berhasil digunakan!
                  </p>
                )}
                {!isLoadingVoucher &&
                  isVoucherCheckAttempted &&
                  !appliedVoucher &&
                  voucherDataFromStore &&
                  voucherDataFromStore.error &&
                  !showVoucherInfoMessage && (
                    <p className="text-red-600 text-xs mt-1">
                      {voucherDataFromStore.error.message || 'Kode voucher tidak valid.'}
                    </p>
                  )}
                {!isLoadingVoucher &&
                  isVoucherCheckAttempted &&
                  !appliedVoucher &&
                  voucherDataFromStore &&
                  !voucherDataFromStore.data &&
                  !voucherDataFromStore.error &&
                  !showVoucherInfoMessage && (
                    <p className="text-red-600 text-xs mt-1">Kode voucher tidak ditemukan.</p>
                  )}
              </div>

              <Separator className="my-4" />

              {/* Final total and checkout */}
              <div className="flex justify-between items-center mb-4">
                <span className="text-base font-semibold">Total Akhir</span>
                <span
                  className={`text-xl font-bold text-primary-600 transition-all duration-300 ${
                    voucherJustApplied ? 'text-green-700 scale-110' : ''
                  }`}
                >
                  {formatCurrency(finalPrice)}
                </span>
              </div>

              <Button
                variant={Button.VARIANTS.SECONDARY}
                className="w-full text-white"
                disabled={isCheckoutDisabled}
                onClick={handleNavigateToPayment}
              >
                <Icon icon="feather:credit-card" className="mr-2 h-5 w-5" />
                Beli ({selectedItemsArray.length} Item)
              </Button>
            </div>
          </div>
        )}

        {/* 🎯 NEW: Delete Confirmation Modal */}
        {deleteModalInfo.isOpen && (
          <Modal
            open={deleteModalInfo.isOpen}
            onClose={handleCancelDelete}
            title={deleteModalInfo.isMultiple ? `Hapus ${selectedItemIds.size} Item` : 'Hapus 1 Item'}
            size={Modal.SIZES.SMALL}
          >
            <div className="pt-2">
              <p className="text-sm text-gray-600 mb-6">
                {deleteModalInfo.isMultiple
                  ? `Apakah kamu yakin ingin menghapus ${selectedItemIds.size} item dari keranjangmu?`
                  : `Apakah kamu yakin ingin menghapus ${deleteModalInfo.itemToDelete?.name || 'item ini'} dari keranjangmu?`}
              </p>
              <div className="flex justify-end space-x-3">
                <Button variant={Button.VARIANTS.SECONDARY_OUTLINE} onClick={handleCancelDelete}>
                  Batal
                </Button>
                <Button
                  variant={Button.VARIANTS.PRIMARY}
                  onClick={handleConfirmDelete}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                >
                  Ya, hapus
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </SheetContent>
    </Sheet>
  );
}
